import React from "react";
import {
	Avatar,
	Box,
	Breadcrumbs,
	Button,
	Card,
	CardContent,
	CircularProgress,
	Container,
	Link as MuiLink,
	Typography
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useAnonymizedTalentPreviewAdmin } from "../../hooks/useAnonymizedTalents";
import { AnonymizedFreelancerDetailsView } from "../../components/anonymizedTalent/AnonymizedFreelancerDetailsView";
import { zoullooColors } from "../../theme";

/**
 * Internal "preview as the client sees it" page. Renders the EXACT same
 * anonymized freelancer fiche a connected client would see for a given profile
 * — same DTO, same `AnonymizedFreelancerDetailsView` component, same
 * translation pipeline. Reachable from the matched-talents grid on the client
 * detail page (`/clients/:id`).
 *
 * Anonymization here applies to the FREELANCER (initials, no photo, fields
 * filtered), not to the client — the page is internal-only and CSMs already
 * have full access to client data on the originating fiche. The `?clientId=`
 * query string is informational only: it just lets the back link return to
 * the originating client file.
 */
const TalentAnonymizedPreview: React.FC = () => {
	const { freelancerProfileId } = useParams<{ freelancerProfileId: string }>();
	const profileId = parseInt(freelancerProfileId ?? "0", 10);

	const [searchParams] = useSearchParams();
	const clientIdParam = searchParams.get("clientId");
	const clientId = clientIdParam ? parseInt(clientIdParam, 10) : null;

	const { t, i18n } = useTranslation();
	const navigate = useNavigate();

	// Match the user's UI language so what the CSM previews here mirrors what
	// the client renders when they toggle their preferred language in-app.
	const lang = (i18n.language?.startsWith("en") ? "en" : "fr") as "fr" | "en";

	const { data: talent, isLoading, isError } = useAnonymizedTalentPreviewAdmin(
		profileId > 0 ? profileId : null,
		lang
	);

	const goBack = () => {
		if (clientId && clientId > 0) {
			navigate(`/clients/${clientId}`);
		} else {
			navigate(-1);
		}
	};

	const initials = talent
		? `${talent.firstNameInitial ?? ""}.${talent.lastNameInitial ?? ""}.`.toUpperCase()
		: "";

	const expertiseLabel = talent?.expertise?.label
		? t(`Expertises.${talent.expertise.label}`, talent.expertise.label)
		: "";
	const experienceLabel = talent?.experience?.label
		? t(`Experiences.${talent.experience.label}`, talent.experience.label)
		: "";

	return (
		<Container maxWidth="lg" sx={{ py: 4 }}>
			<Breadcrumbs sx={{ mb: 2 }} separator="›">
				<MuiLink
					component="button"
					underline="hover"
					color="inherit"
					onClick={goBack}
					sx={{ cursor: "pointer" }}
				>
					{t("anonymizedTalent.previewBackToClient")}
				</MuiLink>
				<Typography color="text.primary">
					{t("anonymizedTalent.previewBreadcrumb")}
				</Typography>
			</Breadcrumbs>

			<Box
				sx={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: { xs: "flex-start", md: "center" },
					flexDirection: { xs: "column", md: "row" },
					gap: 2,
					mb: 3
				}}
			>
				<Box display="flex" alignItems="center" gap={1.5}>
					<VisibilityIcon sx={{ color: zoullooColors.secondary.main }} />
					<Box>
						<Typography
							variant="h2"
							sx={{ color: zoullooColors.secondary.dark, mb: 0.5 }}
						>
							{t("anonymizedTalent.previewTitle")}
						</Typography>
						<Typography
							sx={{ color: zoullooColors.text.secondary, maxWidth: 720 }}
						>
							{t("anonymizedTalent.previewSubtitle")}
						</Typography>
					</Box>
				</Box>
				<Button
					variant="outlined"
					startIcon={<ArrowBackIcon />}
					onClick={goBack}
				>
					{t("anonymizedTalent.previewBackToClient")}
				</Button>
			</Box>

			{isLoading && (
				<Box display="flex" justifyContent="center" sx={{ py: 8 }}>
					<CircularProgress />
				</Box>
			)}

			{isError && !isLoading && (
				<Box
					sx={{
						p: 4,
						textAlign: "center",
						backgroundColor: zoullooColors.error.alpha.lighter,
						border: `1px solid ${zoullooColors.error.light}`,
						borderRadius: 2,
						color: zoullooColors.error.dark
					}}
				>
					{t("anonymizedTalent.previewLoadError")}
				</Box>
			)}

			{!isLoading && !isError && talent && (
				<Card>
					<CardContent>
						{/* Header — same anatomy as MyMatchedTalents.TalentRow so what
						    the CSM sees here matches the client's in-app view 1:1. */}
						<Box display="flex" alignItems="flex-start" gap={3} mb={3}>
							<Avatar
								sx={{
									width: 56,
									height: 56,
									fontWeight: 700,
									fontSize: "1rem",
									backgroundColor: zoullooColors.proposal.magnolia,
									color: zoullooColors.secondary.dark
								}}
							>
								{initials}
							</Avatar>
							<Box flex={1}>
								{/* Headline mirrors the client's closed view: expertise +
								    experience as the title (not the raw profile title), so
								    the CSM preview is 1:1 with what the prospect sees. */}
								<Typography variant="h3" component="span">
									{[expertiseLabel, experienceLabel]
										.filter(Boolean)
										.join(" - ") || t("anonymizedTalent.defaultTitle")}
								</Typography>
								{talent.freelancingYearsOfExperience?.label && (
									<Box mt="1px">
										<Typography variant="body2" color="text.secondary">
											{t("freelancerDetails.freelancerSince")}{" "}
											{t(
												`FreelancingYearsOfExperiences.${talent.freelancingYearsOfExperience.label}`,
												talent.freelancingYearsOfExperience.label
											)}
										</Typography>
									</Box>
								)}
							</Box>
						</Box>

						<AnonymizedFreelancerDetailsView talent={talent} />
					</CardContent>
				</Card>
			)}
		</Container>
	);
};

export default TalentAnonymizedPreview;
